/**
 * Newsletter Modal Handler
 * Clean implementation for newsletter submissions with CAPTCHA
 * - No page jumping or navigation
 * - AJAX submission
 * - Centered modal with success/error messages
 * - Auto-close after 2.5s
 */

class NewsletterModal {
  constructor() {
    this.overlay = null;
    this.isOpen = false;
    this.autoCloseTimer = null;
    this.hCaptchaLoaded = false;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.attachHandlers());
    } else {
      this.attachHandlers();
    }
  }

  attachHandlers() {
    console.log('📧 Newsletter Modal: Attaching form handlers');
    
    // Find all newsletter forms AND contact forms
    const forms = document.querySelectorAll('.newsletter-form, form[action*="contact#newsletter"], form[action*="contact#footer-newsletter"], form[action*="/contact"]');
    
    forms.forEach(form => {
      // Skip if it's a cart or search form
      if (form.action.includes('/cart') || form.action.includes('/search')) {
        return;
      }
      
      // Remove any existing listeners by cloning
      const newForm = form.cloneNode(true);
      form.parentNode.replaceChild(newForm, form);
      
      // Add our handler with highest priority
      newForm.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('📧 Form submitted:', newForm.className || newForm.id);
        this.handleSubmit(newForm);
      }, true);
      
      console.log('✅ Handler attached to:', newForm.className || newForm.id);
    });
  }

  async handleSubmit(form) {
    // Show loading modal
    this.showLoading();
    
    // Determine if this is a newsletter or contact form
    const isNewsletter = form.classList.contains('newsletter-form') || 
                        form.action.includes('#newsletter') || 
                        form.action.includes('#footer-newsletter');
    
    // Try AJAX submission first
    try {
      const result = await this.submitViaAJAX(form);
      
      if (result.needsCaptcha) {
        this.showCaptcha(form, isNewsletter);
      } else if (result.alreadySubscribed) {
        this.showMessage('already', "You're already subscribed.");
      } else if (result.success) {
        const successMessage = isNewsletter 
          ? "You've successfully subscribed." 
          : "Thank you! Your message has been sent.";
        this.showMessage('success', successMessage);
        form.reset();
      } else {
        this.showMessage('error', result.message || (isNewsletter ? 'There was a problem signing up.' : 'There was a problem sending your message.'));
      }
    } catch (error) {
      console.error('❌ AJAX failed:', error);
      // If AJAX fails, show CAPTCHA (Shopify likely requires it)
      this.showCaptcha(form, isNewsletter);
    }
  }

  async submitViaAJAX(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    
    // Convert FormData to URLSearchParams
    for (const [key, value] of formData.entries()) {
      params.append(key, value);
    }
    
    // Ensure customer form type for newsletters
    params.set('form_type', 'customer');
    
    // Clean URL (remove anchors)
    let url = form.action || '/contact';
    if (url.includes('#')) {
      url = url.split('#')[0];
    }
    
    console.log('📤 Submitting to:', url);
    
    const response = await fetch(url, {
      method: 'POST',
      body: params,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });
    
    const text = await response.text();
    const lower = text.toLowerCase();
    
    console.log('📥 Response status:', response.status);
    
    // Handle rate limiting
    if (response.status === 429) {
      return { 
        success: false, 
        message: 'Too many requests. Please wait a moment and try again.' 
      };
    }
    
    // Parse response
    if (lower.includes('already subscribed')) {
      return { success: false, alreadySubscribed: true };
    } else if (lower.includes('captcha') || lower.includes('challenge') || response.status === 422) {
      return { success: false, needsCaptcha: true };
    } else if (response.status === 200 && (lower.includes('success') || lower.includes('subscribed'))) {
      return { success: true };
    } else {
      return { success: false, message: 'There was a problem signing up.' };
    }
  }

  showLoading() {
    this.createOverlay();
    
    this.overlay.innerHTML = `
      <div class="newsletter-modal">
        <div class="newsletter-modal-loading">
          <div class="newsletter-modal-spinner"></div>
          <p>Subscribing...</p>
        </div>
      </div>
    `;
    
    this.open();
  }

  showMessage(type, message) {
    if (!this.overlay) this.createOverlay();
    
    const icons = {
      success: '✓',
      error: '✕',
      already: 'ℹ'
    };
    
    this.overlay.innerHTML = `
      <div class="newsletter-modal">
        <button class="newsletter-modal-close" type="button">×</button>
        <div class="newsletter-modal-message">
          <div class="newsletter-modal-icon">${icons[type] || '•'}</div>
          <p class="newsletter-modal-text">${message}</p>
        </div>
      </div>
    `;
    
    // Add close handler
    this.overlay.querySelector('.newsletter-modal-close').addEventListener('click', () => {
      this.close();
    });
    
    // Allow clicking overlay to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });
    
    this.open();
    
    // Auto-close after 2.5 seconds
    this.autoCloseTimer = setTimeout(() => {
      this.close();
    }, 2500);
  }

  showCaptcha(form, isNewsletter = true) {
    if (!this.overlay) this.createOverlay();
    
    const siteKey = this.getHCaptchaSiteKey();
    const formInputs = this.getFormInputsHTML(form);
    
    this.overlay.innerHTML = `
      <div class="newsletter-modal">
        <button class="newsletter-modal-close" type="button">×</button>
        <div class="newsletter-modal-captcha">
          <h3>Security Verification</h3>
          <p>Please complete the verification to continue:</p>
          <form class="newsletter-captcha-form" method="post" action="${form.action || '/contact'}">
            ${formInputs}
            <div class="newsletter-captcha-widget">
              <div class="h-captcha" data-sitekey="${siteKey}"></div>
            </div>
            <button type="submit" class="newsletter-captcha-submit" disabled>Submit</button>
          </form>
        </div>
      </div>
    `;
    
    // Add close handler
    this.overlay.querySelector('.newsletter-modal-close').addEventListener('click', () => {
      this.close();
    });
    
    // Handle CAPTCHA form submission
    const captchaForm = this.overlay.querySelector('.newsletter-captcha-form');
    const submitBtn = this.overlay.querySelector('.newsletter-captcha-submit');
    
    captchaForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      console.log('🔒 CAPTCHA form submitted');
      
      // Get hCaptcha response
      const hCaptchaResponse = captchaForm.querySelector('[name="h-captcha-response"]');
      if (!hCaptchaResponse || !hCaptchaResponse.value) {
        console.log('❌ No CAPTCHA response');
        return;
      }
      
      // Submit the form via AJAX
      const formData = new FormData(captchaForm);
      const params = new URLSearchParams(formData);
      
      // Clean the action URL
      let actionUrl = captchaForm.action;
      if (actionUrl.includes('#')) {
        actionUrl = actionUrl.split('#')[0];
      }
      if (actionUrl.includes('/challenge')) {
        actionUrl = '/contact';
      }
      
      console.log('📤 Submitting CAPTCHA form to:', actionUrl);
      
      try {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Submitting...';
        
        const response = await fetch(actionUrl, {
          method: 'POST',
          body: params,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          }
        });
        
        const text = await response.text();
        const lower = text.toLowerCase();
        
        console.log('📥 CAPTCHA response status:', response.status);
        
        if (response.status === 429) {
          this.showMessage('error', 'Too many requests. Please wait a moment and try again.');
        } else if (response.status === 200 || response.ok) {
          const successMsg = isNewsletter 
            ? "You've successfully subscribed." 
            : "Thank you! Your message has been sent.";
          this.showMessage('success', successMsg);
          form.reset();
        } else if (lower.includes('already subscribed')) {
          this.showMessage('already', "You're already subscribed.");
        } else {
          const errorMsg = isNewsletter 
            ? 'There was a problem signing up.' 
            : 'There was a problem sending your message.';
          this.showMessage('error', errorMsg);
        }
      } catch (error) {
        console.error('❌ CAPTCHA submission failed:', error);
        this.showMessage('error', 'There was a problem signing up.');
      }
    }, true);
    
    this.open();
    
    // Load hCaptcha
    this.loadHCaptcha(() => {
      if (window.hcaptcha) {
        const widget = this.overlay.querySelector('.h-captcha');
        window.hcaptcha.render(widget, {
          sitekey: siteKey,
          callback: () => {
            console.log('✅ CAPTCHA completed');
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit';
          }
        });
      }
    });
  }

  loadHCaptcha(callback) {
    if (this.hCaptchaLoaded || window.hcaptcha) {
      callback();
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://hcaptcha.com/1/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.hCaptchaLoaded = true;
      callback();
    };
    document.head.appendChild(script);
  }

  getHCaptchaSiteKey() {
    // Try to find existing hCaptcha elements
    const existing = document.querySelector('[data-sitekey]');
    if (existing) {
      return existing.getAttribute('data-sitekey');
    }
    
    // Fallback to Shopify's site key if available
    if (window.Shopify && window.Shopify.recaptchaV3SiteKey) {
      return window.Shopify.recaptchaV3SiteKey;
    }
    
    // Test key as fallback
    return '10000000-ffff-ffff-ffff-000000000001';
  }

  getFormInputsHTML(form) {
    const inputs = [];
    const formData = new FormData(form);
    
    for (const [key, value] of formData.entries()) {
      if (key !== 'h-captcha-response' && key !== 'g-recaptcha-response') {
        inputs.push(`<input type="hidden" name="${key}" value="${value}">`);
      }
    }
    
    // Ensure form_type is customer
    if (!formData.has('form_type')) {
      inputs.push('<input type="hidden" name="form_type" value="customer">');
    }
    
    return inputs.join('');
  }

  createOverlay() {
    if (this.overlay) return;
    
    this.overlay = document.createElement('div');
    this.overlay.className = 'newsletter-modal-overlay';
    document.body.appendChild(this.overlay);
  }

  open() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    document.body.classList.add('newsletter-modal-open');
    
    // Animate in
    requestAnimationFrame(() => {
      if (this.overlay) {
        this.overlay.classList.add('visible');
      }
    });
    
    console.log('✅ Modal opened');
  }

  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    document.body.classList.remove('newsletter-modal-open');
    
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    
    if (this.overlay) {
      this.overlay.classList.remove('visible');
      setTimeout(() => {
        if (this.overlay && this.overlay.parentNode) {
          this.overlay.parentNode.removeChild(this.overlay);
          this.overlay = null;
        }
      }, 300);
    }
    
    console.log('✅ Modal closed');
  }
}

// Initialize when script loads
new NewsletterModal();

